import React, { useState } from 'react';
import CalcShell from '../CalcShell';

export default function LogarithmCalc() {
  const [num, setNum] = useState(1000);
  const [baseMode, setBaseMode] = useState('10');
  const [customBase, setCustomBase] = useState(5);
  const [showWork, setShowWork] = useState(false);
  const base = baseMode === 'e' ? Math.E : baseMode === 'custom' ? customBase : +baseMode;
  const label = baseMode === 'e' ? 'e' : String(base);
  const val = num > 0 && base > 0 && base !== 1 ? Math.log(num)/Math.log(base) : null;
  return (
    <CalcShell look="cnet" title="Logarithm Calculator" subtitle="Compute log base b of x. Supports base 10, base e (natural log), base 2, and any custom base.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Number (x)</label><input type="number" step="any" value={num} onChange={e => setNum(+e.target.value || 0)} data-testid="log-num"/></div>
          <div className="field"><label>Base (b)</label>
            <select value={baseMode} onChange={e => setBaseMode(e.target.value)} data-testid="log-base-mode">
              <option value="10">10 (common log)</option>
              <option value="e">e — Euler's number ≈ 2.71828 (natural log)</option>
              <option value="2">2 (binary log)</option>
              <option value="custom">Custom…</option>
            </select>
          </div>
          {baseMode === 'custom' && (
            <div className="field"><label>Custom Base</label><input type="number" step="any" value={customBase} onChange={e => setCustomBase(+e.target.value || 0)} data-testid="log-custom-base"/></div>
          )}
        </div>
        <div>
          <div className="result-panel">
            <div className="result-title">log<sub>{label}</sub>({num})</div>
            <div className="result-value" data-testid="log-result">{val === null ? 'Undefined' : val.toFixed(8)}</div>
          </div>
          {num > 0 && <table className="data-table mt-4"><tbody>
            <tr><td>Natural log — ln(x) = log<sub>e</sub>(x)</td><td className="text-right">{Math.log(num).toFixed(6)}</td></tr>
            <tr><td>Common log — log<sub>10</sub>(x)</td><td className="text-right">{Math.log10(num).toFixed(6)}</td></tr>
            <tr><td>Binary log — log<sub>2</sub>(x)</td><td className="text-right">{Math.log2(num).toFixed(6)}</td></tr>
          </tbody></table>}
        </div>
      </div>
      <div className="mt-4">
        <button onClick={() => setShowWork(v => !v)} data-testid="log-show-work-toggle"
          className="text-sm px-4 py-1.5 rounded bg-slate-200 hover:bg-slate-300 text-slate-800 font-medium">
          {showWork ? '− Hide working' : '+ Show working'}
        </button>
        {showWork && val !== null && (
          <div className="mt-4 p-5 bg-blue-50 border border-blue-100 rounded-xl" data-testid="log-work-panel">
            <h3 className="text-lg font-bold text-slate-900 mb-3">Step-by-step working</h3>
            <ol className="list-decimal ml-5 space-y-2 text-slate-800 text-[15px] leading-7">
              <li><b>Definition</b>: <span className="font-mono">log<sub>b</sub>(x) = y</span> means <span className="font-mono">b<sup>y</sup> = x</span>.</li>
              <li><b>Change-of-base formula</b>: <span className="font-mono">log<sub>{label}</sub>({num}) = ln({num}) / ln({label}) = {Math.log(num).toFixed(6)} / {Math.log(base).toFixed(6)}</span></li>
              <li><b>Compute</b>: <span className="font-mono">= <b className="text-[#4c7b25]">{val.toFixed(8)}</b></span></li>
              <li><b>Verify</b>: <span className="font-mono">{label}<sup>{val.toFixed(4)}</sup> ≈ {Math.pow(base, val).toFixed(4)} ≈ {num}</span></li>
            </ol>
          </div>
        )}
      </div>
    </CalcShell>
  );
}
